/**
 * bobber_rings.ts — the fishing bobber rings the water (physics follow-up A, 2026-10-05; `audit-feel.md` G6). PURE.
 *
 * The bobber bobbed a sinusoid on the live surface and the cast and the bite were SOUND only — the one floating
 * thing in the game that never touched the shared ripple field (`water_fx.stampWater`, a CPU damped wave equation in a
 * camera-following window; strides, wakes, rain drops and creatures already stamp it). This schedules the stamps:
 *
 *   · the LANDING — one ring where the line lands (a light object: a rain drop ×1.5);
 *   · FLOATING — a faint ring every ~2 s (jittered, so a still lake reads as a bobber riding the swell, not a metronome);
 *   · the BITE — a deeper dip at the bite's onset, then small TUG rings while the fish pulls;
 *   · REELING — a WAKE: a small ring every 0.25 m the bobber travels, plus the fish's tugs.
 *
 * Strengths are in the field's own units (negative = a depression), beside the existing stamps for scale:
 * `RAIN_DROP_STAMP` −0.02 / 0.16 m, `strideStamp` −0.025…−0.045 / 0.3–0.6 m. ABSOLUTE coordinates, like every stamp.
 * Times are WALL seconds (the fishing module's own clock — the server's bite window is real seconds).
 */

export interface RingStamp { x: number; z: number; strength: number; radius: number }
export const BOBBER_LAND = { strength: -0.03, radius: 0.32 } as const;
export const BOBBER_FLOAT = { strength: -0.006, radius: 0.18 } as const;
export const BOBBER_BITE = { strength: -0.035, radius: 0.26 } as const;
export const BOBBER_TUG = { strength: -0.012, radius: 0.2 } as const;
export const BOBBER_WAKE = { strength: -0.01, radius: 0.22 } as const;
/** A floating ring every FLOAT_EVERY_S ± FLOAT_JITTER_S. */
export const FLOAT_EVERY_S = 1.9;
export const FLOAT_JITTER_S = 0.4;
/** A tug ring every TUG_EVERY_S while bitten, every REEL_TUG_EVERY_S while reeling. */
export const TUG_EVERY_S = 0.3;
export const REEL_TUG_EVERY_S = 0.45;
/** A wake ring every WAKE_EVERY_M of bobber travel while reeling. */
export const WAKE_EVERY_M = 0.25;

export type BobberMode = 'float' | 'bite' | 'reel';
export interface BobberRings {
  floatNextS: number; tugNextS: number;
  bitten: boolean;
  wakeX: number; wakeZ: number;
  stamps: number;
}
export function newBobberRings(): BobberRings {
  return { floatNextS: Infinity, tugNextS: Infinity, bitten: false, wakeX: NaN, wakeZ: NaN, stamps: 0 };
}

function jitter01(n: number): number {
  let h = (Math.imul(n | 0, 0x9e3779b1) ^ 0x27d4eb2f) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b) >>> 0;
  return ((h ^ (h >>> 13)) >>> 0) / 4294967296;
}

/** The line lands at (x, z) at wall second `tS`: the landing ring, and the float clock starts. */
export function bobberLanded(s: BobberRings, x: number, z: number, tS: number): RingStamp[] {
  s.bitten = false; s.tugNextS = Infinity; s.wakeX = NaN; s.wakeZ = NaN;
  s.floatNextS = tS + FLOAT_EVERY_S * 0.6;
  s.stamps++;
  return Number.isFinite(x) && Number.isFinite(z) ? [{ x, z, ...BOBBER_LAND }] : [];
}

/** One frame of the bobber at (x, z), wall second `tS`: the rings due now (usually none). Mutates the state. */
export function bobberRings(s: BobberRings, mode: BobberMode, tS: number, x: number, z: number): RingStamp[] {
  if (!Number.isFinite(x) || !Number.isFinite(z) || !Number.isFinite(tS)) return [];
  const out: RingStamp[] = [];
  const add = (st: { strength: number; radius: number }): void => { out.push({ x, z, strength: st.strength, radius: st.radius }); s.stamps++; };
  if (mode === 'float') {
    s.bitten = false;
    if (!(tS < s.floatNextS)) {
      add(BOBBER_FLOAT);
      s.floatNextS = tS + FLOAT_EVERY_S + (jitter01(s.stamps) * 2 - 1) * FLOAT_JITTER_S;
    }
    return out;
  }
  if (!s.bitten) {
    // The bite's onset (a reel that starts without a seen bite takes the dip too): the bobber goes under.
    s.bitten = true;
    add(BOBBER_BITE);
    s.tugNextS = tS + (mode === 'reel' ? REEL_TUG_EVERY_S : TUG_EVERY_S);
    s.wakeX = x; s.wakeZ = z;
    return out;
  }
  if (mode === 'reel') {
    if (!Number.isFinite(s.wakeX)) { s.wakeX = x; s.wakeZ = z; }
    if (Math.hypot(x - s.wakeX, z - s.wakeZ) >= WAKE_EVERY_M) { add(BOBBER_WAKE); s.wakeX = x; s.wakeZ = z; }
  }
  if (!(tS < s.tugNextS)) {
    add(BOBBER_TUG);
    s.tugNextS = tS + (mode === 'reel' ? REEL_TUG_EVERY_S : TUG_EVERY_S);
  }
  return out;
}
